import React from "react";
import useWebSocket, { ReadyState } from "react-use-websocket";
import {
  type BusinessReportOutcomeEvent,
  type BusinessReportOutcomeEvidenceEvent,
  type BusinessReportOutcomeStatusUpdateEvent,
} from "./types";
import { PromptResult } from "./businessReportOutcomeContent";
import { convertToEvidence } from "./helpers";
import { BusinessReportOutcomeDetails } from "@/modules/snapshots/businessReports.types";

interface Props {
  businessReportOutcome: BusinessReportOutcomeDetails;
  setIsComplete: (isComplete: boolean) => void;
  setPromptsResult: React.Dispatch<React.SetStateAction<PromptResult[]>>;
}

export const BusinessReportOutcomeWebsocket: React.FC<Props> = ({
  businessReportOutcome,
  setIsComplete,
  setPromptsResult,
}) => {
  const { lastJsonMessage, readyState, sendJsonMessage } = useWebSocket<
    BusinessReportOutcomeEvent | BusinessReportOutcomeEvidenceEvent | BusinessReportOutcomeStatusUpdateEvent
  >("/pf-business-reports/ws", {
    shouldReconnect: event => event?.code !== 1000,
    reconnectInterval: 3000,
    skipAssert: true,
  });

  React.useEffect(() => {
    if (lastJsonMessage !== null) {
      if (lastJsonMessage?.event === "business_report_outcome_status_update") {
        setIsComplete(true);
      } else if (lastJsonMessage?.event === "business_report_outcome_result") {
        if (businessReportOutcome.status !== "COMPLETED") {
          const msg = lastJsonMessage;

          if (!msg.context) {
            return;
          }

          setPromptsResult(prev =>
            prev.map(row => ({
              ...row,
              content:
                row.business_report_outcome_result_id === msg.business_report_outcome_result_id
                  ? msg.context
                  : row.content,
            })),
          );
        }
      } else {
        const msg = lastJsonMessage as BusinessReportOutcomeEvidenceEvent;

        setPromptsResult(prev =>
          prev.map(row => ({
            ...row,
            evidences:
              row.business_report_prompt_id === msg.business_report_prompt_id
                ? [...row.evidences, convertToEvidence(msg)]
                : row.evidences,
          })),
        );
      }
    }
  }, [lastJsonMessage, setPromptsResult, setIsComplete, businessReportOutcome.status]);

  React.useEffect(() => {
    if (readyState === ReadyState.OPEN) {
      sendJsonMessage({ query_id: businessReportOutcome.id });
    }
  }, [readyState, sendJsonMessage, businessReportOutcome.id]);

  return null;
};
